<template>
  <div class="main-content-container overflow-hidden">
    <PageTitle pageTitle="Testimonials" subTitle="Management" />

    <div class="card bg-white border-0 rounded-3 mb-4">
      <div class="card-body p-0">
        <div class="d-flex justify-content-between align-items-center flex-wrap gap-2 p-4">
          <form class="position-relative table-src-form me-0" @submit.prevent>
            <input type="text" class="form-control" placeholder="Search testimonials..." v-model="searchQuery" />
            <i class="material-symbols-outlined position-absolute top-50 start-0 translate-middle-y">search</i>
          </form>
          <RouterLink to="/testimonials/create" class="btn btn-outline-primary py-1 px-2 px-sm-4 fs-14 fw-medium rounded-3 hover-bg">
            <span class="py-sm-1 d-block">
              <i class="ri-add-line d-none d-sm-inline-block me-1"></i>
              <span>Add New Testimonial</span>
            </span>
          </RouterLink>
        </div>

        <div class="default-table-area style-two default-table-width">
          <div class="table-responsive">
            <table class="table align-middle">
              <thead>
                <tr>
                  <th scope="col">ID</th>
                  <th scope="col">Name</th>
                  <th scope="col">Role</th>
                  <th scope="col">Rating</th>
                  <th scope="col">Text</th>
                  <th scope="col">Status</th>
                  <th scope="col">Actions</th>
                </tr>
              </thead>
              <tbody>
                <tr v-if="loading">
                  <td colspan="7" class="text-center py-4">Loading...</td>
                </tr>
                <tr v-else-if="filteredTestimonials.length === 0">
                  <td colspan="7" class="text-center py-4">No testimonials found.</td>
                </tr>
                <tr v-else v-for="t in filteredTestimonials" :key="t.testimonial_id">
                  <td>#{{ t.testimonial_id }}</td>
                  <td>
                    <div class="d-flex align-items-center">
                      <div class="flex-shrink-0 wh-30 rounded-circle d-flex align-items-center justify-content-center text-white fw-bold fs-12 me-2" :style="{ backgroundColor: t.avatar_color }">
                        {{ t.initials }}
                      </div>
                      <h6 class="mb-0 fs-14 fw-medium">{{ t.name }}</h6>
                    </div>
                  </td>
                  <td class="text-secondary">{{ t.role }}</td>
                  <td>
                    <div class="d-flex gap-1 text-warning">
                      <i v-for="star in 5" :key="star" class="ri-star-fill" :class="star <= t.rating ? 'text-warning' : 'text-muted opacity-25'"></i>
                    </div>
                  </td>
                  <td class="text-body text-truncate" style="max-width: 250px;">{{ t.text }}</td>
                  <td>
                    <span v-if="t.status" class="badge bg-success bg-opacity-10 text-success">Approved</span>
                    <span v-else class="badge bg-warning bg-opacity-10 text-warning">Pending</span>
                    <div v-if="t.order" class="mt-1 fs-12">
                      <RouterLink :to="`/orders/${t.order_id}`" class="text-primary text-decoration-none hover-text-primary">
                        {{ t.order.order_code }}
                      </RouterLink>
                    </div>
                  </td>
                  <td>
                    <div class="d-flex align-items-center gap-1">
                      <RouterLink :to="`/testimonials/${t.testimonial_id}/edit`" class="ps-0 border-0 bg-transparent lh-1 position-relative top-2">
                        <i class="material-symbols-outlined fs-16 text-body">edit</i>
                      </RouterLink>
                      <button @click="deleteTestimonial(t.testimonial_id)" class="ps-0 border-0 bg-transparent lh-1 position-relative top-2">
                        <i class="material-symbols-outlined fs-16 text-danger">delete</i>
                      </button>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, computed, onMounted } from 'vue';
import PageTitle from '@/components/Common/PageTitle.vue';
import testimonialService from '@/services/testimonialService';

export default defineComponent({
  name: 'TestimonialListPage',
  components: {
    PageTitle
  },
  setup() {
    const testimonials = ref<any[]>([]);
    const loading = ref(true);
    const searchQuery = ref('');

    const fetchTestimonials = async () => {
      loading.value = true;
      try {
        const response = await testimonialService.getAll();
        testimonials.value = response.data;
      } catch (error) {
        console.error('Error fetching testimonials:', error);
      } finally {
        loading.value = false;
      }
    };

    const deleteTestimonial = async (id: number) => {
      if (confirm('Are you sure you want to delete this testimonial?')) {
        try {
          await testimonialService.delete(id);
          fetchTestimonials();
        } catch (error) {
          console.error('Error deleting testimonial:', error);
        }
      }
    };

    const filteredTestimonials = computed(() => {
      if (!searchQuery.value) return testimonials.value;
      const q = searchQuery.value.toLowerCase();
      return testimonials.value.filter(t => 
        t.name.toLowerCase().includes(q) || 
        t.role.toLowerCase().includes(q) ||
        t.text.toLowerCase().includes(q)
      );
    });

    onMounted(() => {
      fetchTestimonials();
    });

    return {
      testimonials,
      loading,
      searchQuery,
      filteredTestimonials,
      deleteTestimonial
    };
  }
});
</script>
